import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileVersion, VersionInformation } from '~/shared/data-contract/job-profile-versions';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';

import { createAssignReviewerItem, useAssignReviewer } from '../../api/use-assign-reviewer';
import type { JobItemDetails } from '../../api/use-job-item-details';
import { GenericAssignReviewerBadge } from '../../components/ReviewerComponents';
import { useTrackWorkflowTransition } from '../../hooks/analytics/use-track-workflow-transition';
import { usePublishAction } from '../../hooks/use-publish-action';
import { useReviewAction } from '../../hooks/use-review-action';
import { ChatAwareActionBadge } from './ChatAwareActionBadge';
import type { AuditTableStatus } from './types';

const SummaryReviewerBadge: FunctionComponent<{
    readonly architectureId: string;
    readonly chromeVariant?: 'gray' | 'warning';
    readonly isArchived: boolean;
    readonly item: JobItemDetails;
    readonly onInviteReviewer?: () => void;
}> = ({ architectureId, chromeVariant = 'gray', isArchived, item, onInviteReviewer }) => (
    <GenericAssignReviewerBadge
        badgeClassName="!h-[24px] tracking-tight hover:!border-gray-400"
        chromeVariant={chromeVariant}
        disabled={isArchived}
        isSelected={false}
        item={item}
        itemType={item.type}
        jobArchitectureId={architectureId}
        jobClusterId={item.parentId}
        onInvite={onInviteReviewer}
        onlyAssignReviewer={true}
        showOnlyWhenUnassigned={false}
    />
);

const getParentRoleId = (item: JobItemDetails): string | undefined =>
    item.type === 'job-level' && item.parentId != null ? item.parentId : undefined;

export const CollapsedAuditAction: FunctionComponent<{
    readonly architectureId: string;
    readonly currentVersion: JobProfileVersion;
    readonly isArchived: boolean;
    readonly item: JobItemDetails;
    readonly onInviteReviewer?: () => void;
    readonly status: AuditTableStatus;
    readonly toggle?: {
        readonly icon: ReactNode;
        readonly label: string;
        readonly onClick: () => void;
    };
}> = ({ architectureId, currentVersion, isArchived, item, onInviteReviewer, status, toggle }) => {
    const translate = useTranslate();
    const { applicationState } = useApplicationContext();
    const { mutateAsync: assignReviewer } = useAssignReviewer();
    const { trackWorkflowTransition } = useTrackWorkflowTransition({ architectureId });
    const workflowItem = useMemo(() => {
        const versionInfo: VersionInformation = {
            ...item.versionInfo,
            comment: item.versionInfo?.comment ?? currentVersion.comment,
            createdAt: item.versionInfo?.createdAt ?? currentVersion.createdAt,
            isPublished: status === 'published',
            isReviewed: status === 'reviewed' || status === 'published',
            publishedAt: item.versionInfo?.publishedAt ?? currentVersion.publishedAt,
            publishedBy: item.versionInfo?.publishedBy ?? currentVersion.publishedBy,
            reviewedAt: item.versionInfo?.reviewedAt ?? currentVersion.reviewedAt ?? undefined,
            translationOriginLanguage: item.versionInfo?.translationOriginLanguage,
            version: currentVersion.version,
        };

        return { ...item, versionInfo };
    }, [currentVersion, item, status]);
    const handleAssignMyself = useCallback(async (): Promise<void> => {
        const id = applicationState.myUserProfile.get('id').unwrapOr('');
        const firstName = applicationState.myUserProfile.get('firstName').unwrapOr('');
        const lastName = applicationState.myUserProfile.get('lastName').unwrapOr('');
        const clusterId = item.parentId;

        if (id.length === 0 || clusterId == null) {
            return;
        }

        await assignReviewer({
            clusterId,
            item: createAssignReviewerItem(
                workflowItem,
                { id, firstName, lastName, state: 'assigned' },
                workflowItem.type,
            ),
            updateType: item.reviewer === undefined ? 'create' : 'update',
        });

        trackWorkflowTransition({
            actionType: 'assign_reviewer',
            itemId: item.id,
            itemType: item.type,
            parentRoleId: getParentRoleId(item),
            targetUserId: id,
            toStatus: 'in_review',
            versionId: currentVersion.version.toString(),
        });
    }, [
        applicationState.myUserProfile,
        assignReviewer,
        currentVersion.version,
        item,
        trackWorkflowTransition,
        workflowItem,
    ]);
    const { handleReview } = useReviewAction({
        architectureId,
        item: workflowItem,
        itemType: item.type,
    });
    const { handlePublish } = usePublishAction({
        architectureId,
        item: workflowItem,
        itemType: item.type,
    });

    const toggleButton =
        toggle === undefined ? null : (
            <Button
                aria-label={toggle.label}
                iconOnly={true}
                size="sm"
                type="button"
                variant="link"
                onClick={toggle.onClick}
            >
                {toggle.icon}
            </Button>
        );

    const renderAction = (
        label: string,
        variant: 'info' | 'success',
        onClick: () => void,
        leading?: ReactNode,
    ): ReactNode => (
        <div className="flex shrink-0 items-center gap-xs">
            {leading}
            <ChatAwareActionBadge
                label={label}
                testId={variant === 'info' ? 'review-badge' : 'publish-badge'}
                variant={variant}
                onClick={onClick}
            />
            {toggleButton}
        </div>
    );

    if (status === 'draft' && !isArchived) {
        if (is.nullOrUndefined(item.reviewer)) {
            return (
                <div className="flex shrink-0 items-center gap-xs">
                    {item.actions.canAssignReviewer === true ? (
                        <Badge variant="gray" onClick={() => void handleAssignMyself()}>
                            {translate('pages.job-architecture-v2.prompts.assign-myself')}
                        </Badge>
                    ) : null}
                    <SummaryReviewerBadge
                        architectureId={architectureId}
                        chromeVariant="warning"
                        isArchived={isArchived}
                        item={item}
                        onInviteReviewer={onInviteReviewer}
                    />
                    {toggleButton}
                </div>
            );
        }

        return renderAction(
            translate('pages.job-architecture.mark-as-reviewed'),
            'info',
            handleReview,
            <SummaryReviewerBadge
                architectureId={architectureId}
                isArchived={isArchived}
                item={item}
                onInviteReviewer={onInviteReviewer}
            />,
        );
    }

    if (status === 'reviewed' && !isArchived && item.actions.canPublish === true) {
        return renderAction(translate('pages.job-architecture-v2.job-role-publish-button'), 'success', handlePublish);
    }

    return toggleButton;
};
